import { readWorkstationBinding } from './workstationIdentity';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from './api';
import { getTerminalScope, readTerminalReader, saveTerminalReader } from './terminalPayments';
import { configureNativeTerminal, discoverNativeTerminalReaders, connectNativeTerminalReader,
  isNativeTerminalAvailable, disconnectNativeTerminal } from './stripeTerminalNative';

const nativeMessage = event => (event?.state === 'update_installing' && Number.isFinite(event.progress)
  ? `Mise à jour du lecteur : ${Math.round(Math.max(0, Math.min(1, event.progress)) * 100)} %. Laissez-le alimenté et connecté.` : ({
  discovering: 'Recherche des lecteurs à proximité…', connecting: 'Connexion au lecteur…',
  connected: 'Lecteur connecté.', reconnecting: 'Reconnexion au lecteur…',
  update_installing: 'Mise à jour du lecteur en cours. Laissez-le alimenté et connecté.',
  update_complete: 'Mise à jour terminée.', update_failed: 'Mise à jour interrompue. Vérifiez la batterie et la connexion du lecteur.',
  low_battery: 'Batterie du lecteur faible : branchez-le pour continuer.',
  discovery_finished: 'Recherche terminée. Choisissez le lecteur trouvé ou relancez la recherche.',
  discovery_stopped: 'Recherche interrompue. Vérifiez les autorisations et la connexion.', disconnected: 'Lecteur déconnecté.',
}[event?.state] || 'Connexion au lecteur en cours…'));

function TerminalSettings({ request }) {
  const scope = useRef(getTerminalScope()).current;
  const boundWorkstation = readWorkstationBinding();
  const [configuration, setConfiguration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [readerId, setReaderId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [localReader, setLocalReader] = useState(() => readTerminalReader(scope));
  const [locationId, setLocationId] = useState('');
  const [connection, setConnection] = useState('usb');
  const [discoveredReaders, setDiscoveredReaders] = useState([]);
  const [nativeStatus, setNativeStatus] = useState('');
  const live = useRef(false);
  const busy = useRef(false);
  const call = useCallback(async (path = '', method = 'GET', body) => {
    if (getTerminalScope() !== scope) throw new Error('Compte modifié : rechargez la configuration.');
    const response = await request(`${API_URL}/api/admin/payments/stripe/terminal${path}`, {
      method, cache: 'no-store', ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
    });
    if (getTerminalScope() !== scope) throw new Error('Compte modifié : rechargez la configuration.');
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error([401, 403].includes(response.status)
      ? 'Cette configuration est réservée à l’administrateur.'
      : payload?.error || 'Le service des terminaux est indisponible. Réessayez.');
    return payload;
  }, [request, scope]);

  const refresh = useCallback(async () => {
    const data = await call();
    if (typeof data?.enabled !== 'boolean' || !Array.isArray(data.readers) || !Array.isArray(data.locations)) throw new Error('Configuration TPE incomplète. Réessayez.');
    if (!live.current) return;
    setConfiguration(data); setEnabled(data.enabled); setReaderId(data.defaultReaderId || '');
    setLocationId(current => data.locations.some(row => row.id === current) ? current : data.locations[0]?.id || '');
  }, [call]);

  const perform = useCallback(async (action, success = '') => {
    if (busy.current) return;
    busy.current = true; setLoading(true); setError(''); setNotice('');
    try { await action(); if (live.current && success) setNotice(success); }
    catch (failure) { if (live.current) setError(failure.message || 'Opération impossible.'); }
    finally { busy.current = false; if (live.current) setLoading(false); }
  }, []);

  useEffect(() => {
    live.current = true;
    perform(refresh);
    return () => { live.current = false; };
  }, [refresh, perform]);

  async function createLocation(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    await perform(async () => {
      await call('/locations', 'POST', { displayName: values.get('displayName'), address: {
        line1: values.get('line1'), city: values.get('city'), postalCode: values.get('postalCode'), country: 'FR',
      } });
      if (!live.current) return;
      form.reset(); await refresh();
    }, 'Emplacement créé sur le compte Stripe de votre établissement.');
  }

  async function registerReader(event) {
    event.preventDefault();
    const form = event.currentTarget; const values = new FormData(form);
    await perform(async () => {
      await call('/readers', 'POST', { registrationCode: values.get('registrationCode'), label: values.get('label'), locationId });
      if (!live.current) return;
      form.reset(); await refresh();
    }, 'Lecteur enregistré. Vous pouvez maintenant l’affecter à ce poste.');
  }

  async function discoverReaders() {
    await perform(async () => {
      setDiscoveredReaders([]); setNativeStatus('Préparation de la connexion…');
      await configureNativeTerminal({ locationId, stripeAccountId: configuration.stripeAccountId,
        livemode: configuration.livemode, fetchWithAuth: request, transport: connection, scope,
        onReaders: readers => { if (live.current) setDiscoveredReaders(readers); },
        onStatus: event => { if (live.current) setNativeStatus(nativeMessage(event)); },
      });
      if (live.current) await discoverNativeTerminalReaders();
    });
  }

  async function associateReader(serialNumber) {
    await perform(async () => {
      const reader = await connectNativeTerminalReader({ serialNumber });
      if (!live.current) return;
      saveTerminalReader(reader.readerId, scope); setLocalReader(reader.readerId);
      await refresh(); setReaderId(reader.readerId);
    }, 'WisePad 3 associé à cet appareil. Enregistrez ensuite l’activation pour l’établissement.');
  }

  const canConfigure = configuration?.canManage === true;
  return <section className="admin-panel stripe-connect" aria-labelledby="terminal-settings-title">
    <div className="admin-panel-head"><span className="admin-panel-kicker">Encaissement en présence du client</span><h2 id="terminal-settings-title">Terminaux Stripe</h2></div>
    <p>Les paiements du TPE arrivent sur le compte Stripe de cet établissement. Un ticket est soldé uniquement après confirmation du paiement par Stripe.</p>
    <p>Le WisePad 3 se connecte à l’application Android en USB ou Bluetooth. La connexion Internet reste nécessaire pour cette intégration.</p>
    {configuration ? <p role="status">{configuration.enabled ? 'Encaissement Stripe activé pour cet établissement.' : 'Encaissement Stripe sur TPE non activé.'}{configuration.mode === 'test' ? ' Mode test : aucun encaissement réel.' : ''}</p> : null}
    {configuration?.message ? <p>{configuration.message}</p> : null}
    {error ? <p role="alert" className="stripe-connect-error">{error}</p> : null}
    {notice ? <p role="status">{notice}</p> : null}
    <button className="admin-button admin-button-secondary" disabled={loading} onClick={() => perform(refresh)}>Actualiser les terminaux</button>
    {canConfigure ? <>
      <form className="admin-form" onSubmit={createLocation}>
        <h3>Emplacement du restaurant</h3>
        <label className="admin-field">Nom de l’emplacement<input name="displayName" required maxLength={100} autoComplete="organization" /></label>
        <label className="admin-field">Adresse<input name="line1" required maxLength={150} autoComplete="address-line1" /></label>
        <label className="admin-field">Ville<input name="city" required maxLength={100} autoComplete="address-level2" /></label>
        <label className="admin-field">Code postal<input name="postalCode" required pattern="[0-9]{5}" autoComplete="postal-code" /></label>
        <p>France — indiquez l’adresse réelle où le lecteur sera utilisé.</p>
        <button className="admin-button" disabled={loading} type="submit">Créer l’emplacement dans Stripe</button>
      </form>
      <label className="admin-field">Emplacement du lecteur<select value={locationId} onChange={event => setLocationId(event.target.value)} disabled={loading}>
        <option value="">Choisir un emplacement</option>
        {configuration.locations.map(location => <option key={location.id} value={location.id}>{location.displayName || location.display_name || location.id}</option>)}
      </select></label>
      <div className="admin-form">
        <h3>WisePad 3 sur cet appareil Android</h3>
        <label className="admin-field">Connexion<select value={connection} onChange={event => setConnection(event.target.value)} disabled={loading}><option value="usb">USB</option><option value="bluetooth">Bluetooth</option></select></label>
        <p>Ouvrez cette page dans l’application Android pour connecter le lecteur. L’association doit être faite par un membre de l’équipe avant de proposer la borne au client.</p>
        <button className="admin-button" type="button" disabled={loading || !locationId || !isNativeTerminalAvailable()} onClick={discoverReaders}>Rechercher le WisePad 3</button>
        {nativeStatus ? <p role="status">{nativeStatus}</p> : null}
        {discoveredReaders.length ? <ul>{discoveredReaders.map(reader => <li key={reader.serialNumber}>
          WisePad 3 — {reader.serialNumber}{' '}
          <button className="admin-button" type="button" disabled={loading} onClick={() => associateReader(reader.serialNumber)}>Associer ce lecteur</button>
        </li>)}</ul> : null}
        {isNativeTerminalAvailable() ? <button className="admin-button admin-button-secondary" type="button" disabled={loading} onClick={() => perform(async () => {
          await disconnectNativeTerminal(); if (live.current) { setDiscoveredReaders([]); setNativeStatus('Lecteur déconnecté.'); }
        })}>Déconnecter le lecteur</button> : null}
      </div>
      <details><summary>Enregistrer un lecteur connecté à Internet</summary>
        <p>Cette procédure avec code concerne les lecteurs intelligents compatibles, pas le WisePad 3.</p>
        <form className="admin-form" onSubmit={registerReader}>
          <label className="admin-field">Nom du lecteur<input name="label" required maxLength={100} /></label>
          <label className="admin-field">Code d’enregistrement affiché sur le lecteur<input name="registrationCode" required maxLength={100} autoComplete="off" /></label>
          <button className="admin-button" disabled={loading || !locationId} type="submit">Enregistrer ce lecteur dans Stripe</button>
        </form>
      </details>
      <form className="admin-form" onSubmit={event => { event.preventDefault(); perform(async () => {
        await call('/configuration', 'PUT', { enabled, defaultReaderId: readerId || null });
        if (live.current) await refresh();
      }, 'Configuration de l’établissement enregistrée.'); }}>
        <h3>Activer l’encaissement</h3>
        <label className="admin-field">Lecteur par défaut<select value={readerId} onChange={event => setReaderId(event.target.value)} disabled={loading}>
          <option value="">Choisir un lecteur</option>
          {configuration.readers.map(reader => <option key={reader.id} value={reader.id}>{reader.label || reader.id} — {reader.deviceType} ({reader.status || 'état inconnu'})</option>)}
        </select></label>
        <label className="stripe-connect-consent"><input type="checkbox" checked={enabled} onChange={event => setEnabled(event.target.checked)} disabled={loading} /><span>Utiliser Stripe pour les encaissements CB de cet établissement. La confirmation manuelle d’un paiement CB et l’encaissement CB hors ligne seront désactivés.</span></label>
        <p>Les tickets sont encaissés séparément et intégralement. Pour un paiement partagé, préparez les tickets avant de lancer le lecteur. Les remboursements se gèrent dans Stripe et doivent être rapprochés séparément dans la caisse.</p>
        <button className="admin-button" disabled={loading || (enabled && !readerId)} type="submit">Enregistrer l’activation</button>
      </form>
      {boundWorkstation ? <p>Le terminal de ce poste se configure dans Administration → Imprimantes → Postes de travail. Cette affectation prime sur le lecteur par défaut.</p> : <form className="admin-form" onSubmit={event => { event.preventDefault(); perform(async () => {
        if (!configuration.readers.some(reader => reader.id === localReader)) throw new Error('Choisissez un lecteur de cet établissement.');
        saveTerminalReader(localReader, scope);
      }, 'Lecteur affecté à ce poste et à cet établissement.'); }}>
        <label className="admin-field">Lecteur de ce poste<select value={localReader} onChange={event => setLocalReader(event.target.value)} disabled={loading}>
          <option value="">Choisir un lecteur</option>
          {configuration.readers.map(reader => <option key={reader.id} value={reader.id}>{reader.label || reader.id}</option>)}
        </select></label>
        <button className="admin-button" disabled={loading || !localReader} type="submit">Affecter à ce poste</button>
      </form>}
    </> : null}
  </section>;
}

export default function StripeTerminalSettings({ scopeKey, ...props }) {
  return <TerminalSettings key={scopeKey} {...props} />;
}
